/**
 * UranIA PA — Cowork (``/cowork``).
 *
 * Redazione assistita di un documento .docx tramite la skill di sistema
 * ``skill_cowork`` (focus-only document drafting). Flusso:
 *
 *   1. Scelta del template (``tipo_documento`` esistente).
 *   2. Allegati opzionali (PDF/DOCX/TXT) con motivo.
 *   3. Chat focalizzata: la skill chiede informazioni mirate al
 *      documento, attinge dal corpus RAG, NON inventa nulla.
 *   4. Generazione DOCX (1 chiamata Gateway per sezione del template,
 *      assemblaggio python-docx server-side) + download.
 *
 * UX guard: route riservata a ``funzionario_adg`` + admin (per test).
 * Onboarding richiesto come per il workspace Mod5 (lo stesso hook
 * ``useFunzionarioOnboardingStatus`` viene riusato).
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, JSX } from 'react';

import { OnboardingRequiredScreen } from '../../components/workspace/OnboardingRequiredScreen.tsx';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../lib/auth/auth-store.ts';
import {
  principalIsAnyAdmin,
  principalIsFunzionario,
} from '../../lib/auth/rbac.ts';
import { useFunzionarioOnboardingStatus } from '../../lib/onboarding/use-funzionario-onboarding-status.ts';
import {
  listTipiVisible,
  TipiDocumentoApiError,
  type ListTipiDocumentoDto,
} from '../../lib/admin/tipi-documento-api.ts';
import {
  CoworkApiError,
  createCoworkSession,
  downloadCoworkDocument,
  generateCoworkDocument,
  getCoworkSession,
  postCoworkMessage,
  uploadCoworkAttachment,
  type CoworkSessionDto,
} from '../../lib/workspace/cowork-api.ts';
import { Route as RootRoute } from '../__root.tsx';

const ACCEPTED_MIME = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

function StatusBadge(props: { readonly status: CoworkSessionDto['status'] }): JSX.Element {
  const { t } = useTranslation();
  const map: Record<
    CoworkSessionDto['status'],
    { bg: string; fg: string; label: string }
  > = {
    collecting: { bg: '#cfe2ff', fg: '#084298', label: t('cowork.status.collecting') },
    drafting: { bg: '#fff3cd', fg: '#664d03', label: t('cowork.status.drafting') },
    ready: { bg: '#d1e7dd', fg: '#0f5132', label: t('cowork.status.ready') },
    failed: { bg: '#f8d7da', fg: '#842029', label: t('cowork.status.failed') },
  };
  const c = map[props.status];
  return (
    <span
      className="badge"
      style={{ backgroundColor: c.bg, color: c.fg, fontSize: '0.65rem' }}
    >
      {c.label}
    </span>
  );
}

interface TemplatePickerProps {
  readonly onPicked: (tipoId: string) => void;
  readonly isCreating: boolean;
  readonly createError: string | null;
}

function TemplatePicker(props: TemplatePickerProps): JSX.Element {
  const { t } = useTranslation();
  const selectId = useId();
  const tipiQuery = useQuery<ListTipiDocumentoDto, TipiDocumentoApiError>({
    queryKey: ['cowork', 'tipi-visible'],
    queryFn: listTipiVisible,
    staleTime: 30_000,
  });
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    const first = tipiQuery.data?.items[0];
    if (selected.length === 0 && first !== undefined) {
      setSelected(first.tipo_id);
    }
  }, [tipiQuery.data, selected.length]);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (selected.length > 0) props.onPicked(selected);
  };

  return (
    <form
      onSubmit={onSubmit}
      className="card shadow-sm border-0"
      data-testid="cowork-template-picker"
    >
      <div className="card-body">
        <h2 className="h5 mb-2">{t('cowork.template.heading')}</h2>
        <p className="small text-muted mb-3">{t('cowork.template.subtitle')}</p>
        {tipiQuery.isLoading && <p>{t('cowork.template.loading')}</p>}
        {tipiQuery.isError && (
          <div className="alert alert-danger small" role="alert">
            {tipiQuery.error.detail ?? tipiQuery.error.message}
          </div>
        )}
        {tipiQuery.isSuccess && tipiQuery.data.items.length === 0 && (
          <div className="alert alert-warning small" role="alert">
            {t('cowork.template.empty')}
          </div>
        )}
        {tipiQuery.isSuccess && tipiQuery.data.items.length > 0 && (
          <div className="mb-3">
            <label htmlFor={selectId} className="form-label small text-muted">
              {t('cowork.template.field_label')}
            </label>
            <select
              id={selectId}
              className="form-select"
              value={selected}
              onChange={(e): void => setSelected(e.target.value)}
              data-testid="cowork-template-select"
            >
              {tipiQuery.data.items.map((tipo) => (
                <option key={tipo.tipo_id} value={tipo.tipo_id}>
                  {tipo.label}
                </option>
              ))}
            </select>
            {(() => {
              const meta = tipiQuery.data.items.find((tp) => tp.tipo_id === selected);
              if (!meta) return null;
              return (
                <p className="small text-muted mt-2 mb-0">
                  {meta.descrizione || meta.titolo_docx}
                </p>
              );
            })()}
          </div>
        )}
        {props.createError !== null && (
          <div className="alert alert-danger small" role="alert">
            {props.createError}
          </div>
        )}
        <div className="d-flex justify-content-end gap-2">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              tipiQuery.isLoading ||
              selected.length === 0 ||
              props.isCreating ||
              (tipiQuery.isSuccess && tipiQuery.data.items.length === 0)
            }
            data-testid="cowork-template-start"
          >
            {props.isCreating ? t('cowork.template.starting') : t('cowork.template.start')}
          </button>
        </div>
      </div>
    </form>
  );
}

interface AttachmentsPanelProps {
  readonly session: CoworkSessionDto;
  readonly onUpload: (file: File, motivo: string) => Promise<void>;
  readonly disabled: boolean;
}

function AttachmentsPanel(props: AttachmentsPanelProps): JSX.Element {
  const { t } = useTranslation();
  const fileInputId = useId();
  const motivoInputId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [motivo, setMotivo] = useState('');
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (file === null) return;
    if (!ACCEPTED_MIME.includes(file.type)) {
      setErr(t('cowork.attachments.errors.mime'));
      return;
    }
    setUploading(true);
    setErr(null);
    try {
      await props.onUpload(file, motivo);
      setFile(null);
      setMotivo('');
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : t('cowork.attachments.errors.generic'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card shadow-sm border-0" data-testid="cowork-attachments">
      <div className="card-header bg-white border-bottom py-2 px-3">
        <span
          className="text-uppercase text-muted fw-semibold"
          style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
        >
          {t('cowork.attachments.heading')}
        </span>
      </div>
      <div className="card-body">
        {props.session.attachments.length === 0 ? (
          <p className="small text-muted fst-italic mb-3">
            {t('cowork.attachments.empty')}
          </p>
        ) : (
          <ul className="list-unstyled small mb-3">
            {props.session.attachments.map((a, idx) => (
              <li key={`${a.filename}-${idx}`} className="mb-1">
                <code className="text-dark">{a.filename}</code>{' '}
                <span className="text-muted">({Math.round(a.size_bytes / 1024)} KB)</span>
                {a.motivo.length > 0 && (
                  <div className="text-muted fst-italic small">{a.motivo}</div>
                )}
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={onSubmit}>
          <div className="mb-2">
            <label htmlFor={fileInputId} className="form-label small text-muted mb-1">
              {t('cowork.attachments.field_file')}
            </label>
            <input
              id={fileInputId}
              type="file"
              className="form-control form-control-sm"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              onChange={(e): void => setFile(e.target.files?.[0] ?? null)}
              disabled={props.disabled || uploading}
              data-testid="cowork-attachment-file"
            />
          </div>
          <div className="mb-2">
            <label htmlFor={motivoInputId} className="form-label small text-muted mb-1">
              {t('cowork.attachments.field_motivo')}
            </label>
            <input
              id={motivoInputId}
              type="text"
              className="form-control form-control-sm"
              value={motivo}
              onChange={(e): void => setMotivo(e.target.value)}
              placeholder={t('cowork.attachments.field_motivo_placeholder')}
              maxLength={500}
              disabled={props.disabled || uploading}
            />
          </div>
          {err !== null && (
            <div className="text-danger small mb-2" role="alert">
              {err}
            </div>
          )}
          <button
            type="submit"
            className="btn btn-sm btn-outline-primary w-100"
            disabled={file === null || uploading || props.disabled}
            data-testid="cowork-attachment-upload"
          >
            {uploading ? t('cowork.attachments.uploading') : t('cowork.attachments.upload')}
          </button>
        </form>
      </div>
    </div>
  );
}

interface ChatPanelProps {
  readonly session: CoworkSessionDto;
  readonly onSend: (content: string) => Promise<void>;
  readonly sending: boolean;
  readonly sendError: string | null;
}

function ChatPanel(props: ChatPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current !== null) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [props.session.messages.length, props.sending]);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = input.trim();
    if (trimmed.length === 0) return;
    setInput('');
    await props.onSend(trimmed);
  };

  const disabledChat = props.session.status === 'drafting';

  return (
    <div className="card shadow-sm border-0" data-testid="cowork-chat">
      <div className="card-header bg-white border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
        <span
          className="text-uppercase text-muted fw-semibold"
          style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
        >
          {t('cowork.chat.heading')}
        </span>
        <StatusBadge status={props.session.status} />
      </div>
      <div
        ref={scrollRef}
        className="card-body"
        style={{ minHeight: 320, maxHeight: 460, overflowY: 'auto' }}
        data-testid="cowork-chat-history"
      >
        {props.session.messages.length === 0 && (
          <p className="text-muted fst-italic small mb-0">
            {t('cowork.chat.empty_hint')}
          </p>
        )}
        {props.session.messages.map((m, idx) => (
          <div
            key={`${m.created_at}-${idx}`}
            className={`mb-3 d-flex ${m.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
          >
            <div
              className="rounded p-2 px-3"
              style={{
                backgroundColor: m.role === 'user' ? '#e7f1ff' : '#f8f9fa',
                color: '#212529',
                maxWidth: '85%',
                whiteSpace: 'pre-wrap',
                fontSize: '0.9rem',
              }}
            >
              <div
                className="small text-muted text-uppercase mb-1"
                style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}
              >
                {m.role === 'user' ? t('cowork.chat.user') : t('cowork.chat.assistant')}
              </div>
              {m.content}
            </div>
          </div>
        ))}
        {props.sending && (
          <div className="text-muted small fst-italic">{t('cowork.chat.thinking')}</div>
        )}
      </div>
      <div className="card-footer bg-white border-top">
        {props.sendError !== null && (
          <div className="alert alert-danger small mb-2 py-2" role="alert">
            {props.sendError}
          </div>
        )}
        <form onSubmit={onSubmit} className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            value={input}
            onChange={(e): void => setInput(e.target.value)}
            placeholder={t('cowork.chat.input_placeholder')}
            disabled={disabledChat || props.sending}
            data-testid="cowork-chat-input"
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={input.trim().length === 0 || disabledChat || props.sending}
            data-testid="cowork-chat-send"
          >
            {t('cowork.chat.send')}
          </button>
        </form>
      </div>
    </div>
  );
}

interface DocumentPanelProps {
  readonly session: CoworkSessionDto;
  readonly onGenerate: () => Promise<void>;
  readonly onDownload: () => Promise<void>;
  readonly generating: boolean;
  readonly generateError: string | null;
}

function DocumentPanel(props: DocumentPanelProps): JSX.Element {
  const { t } = useTranslation();
  const canGenerate =
    props.session.messages.length >= 2 && props.session.status !== 'drafting';

  return (
    <div className="card shadow-sm border-0" data-testid="cowork-document">
      <div className="card-header bg-white border-bottom py-2 px-3">
        <span
          className="text-uppercase text-muted fw-semibold"
          style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
        >
          {t('cowork.document.heading')}
        </span>
      </div>
      <div className="card-body">
        <p className="small text-muted mb-3">{t('cowork.document.hint')}</p>
        {props.session.error_detail !== null && (
          <div className="alert alert-warning small" role="alert">
            <strong>{t('cowork.document.error_title')}</strong>
            <p className="mb-0">{props.session.error_detail}</p>
          </div>
        )}
        {props.generateError !== null && (
          <div className="alert alert-danger small" role="alert">
            {props.generateError}
          </div>
        )}
        <div className="d-flex flex-column gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={(): void => {
              void props.onGenerate();
            }}
            disabled={!canGenerate || props.generating}
            data-testid="cowork-generate"
          >
            {props.generating
              ? t('cowork.document.generating')
              : props.session.status === 'ready'
                ? t('cowork.document.regenerate')
                : t('cowork.document.generate')}
          </button>
          <button
            type="button"
            className="btn btn-outline-success"
            disabled={!props.session.document_available}
            onClick={(): void => {
              void props.onDownload();
            }}
            data-testid="cowork-download"
          >
            {t('cowork.document.download')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CoworkPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isFunzionario = principalIsFunzionario(principal);
  const isAdmin = principalIsAnyAdmin(principal);
  const onboardingStatus = useFunzionarioOnboardingStatus();
  const qc = useQueryClient();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const sessionQuery = useQuery<CoworkSessionDto, CoworkApiError>({
    queryKey: ['cowork', 'session', sessionId],
    queryFn: () => getCoworkSession(sessionId as string),
    enabled: sessionId !== null,
    staleTime: 1_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data === undefined) return false;
      return data.status === 'drafting' ? 3_000 : false;
    },
  });

  const createMutation = useMutation<CoworkSessionDto, CoworkApiError, string>({
    mutationFn: createCoworkSession,
    onSuccess: (s) => {
      setSessionId(s.session_id);
      setCreateError(null);
    },
    onError: (e) => {
      setCreateError(e.detail ?? e.message);
    },
  });

  const sendMutation = useMutation<CoworkSessionDto, CoworkApiError, string>({
    mutationFn: (content) => postCoworkMessage(sessionId as string, content),
    onSuccess: (updated) => {
      qc.setQueryData(['cowork', 'session', sessionId], updated);
      setSendError(null);
    },
    onError: (e) => setSendError(e.detail ?? e.message),
  });

  const uploadMutation = useMutation<
    CoworkSessionDto,
    CoworkApiError,
    { file: File; motivo: string }
  >({
    mutationFn: ({ file, motivo }) =>
      uploadCoworkAttachment(sessionId as string, file, motivo),
    onSuccess: (updated) =>
      qc.setQueryData(['cowork', 'session', sessionId], updated),
  });

  const generateMutation = useMutation<CoworkSessionDto, CoworkApiError>({
    mutationFn: () => generateCoworkDocument(sessionId as string),
    onSuccess: (updated) => {
      qc.setQueryData(['cowork', 'session', sessionId], updated);
      setGenerateError(null);
    },
    onError: (e) => setGenerateError(e.detail ?? e.message),
  });

  const onDownload = async (): Promise<void> => {
    if (sessionId === null) return;
    try {
      const blob = await downloadCoworkDocument(sessionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cowork-${sessionId.slice(0, 8)}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'download_failed';
      setGenerateError(msg);
    }
  };

  // ------------------- Guards -------------------

  if (!isAuthenticated) {
    return (
      <section
        className="container py-4"
        data-testid="cowork-unauthenticated"
      >
        <h1 className="h3 mb-3">{t('cowork.title')}</h1>
        <div className="alert alert-info">
          <p className="mb-2">{t('cowork.unauthenticated.message')}</p>
          <Link to="/login" className="btn btn-primary">
            {t('cowork.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }
  if (!isFunzionario && !isAdmin) {
    return (
      <section className="container py-4" data-testid="cowork-forbidden">
        <h1 className="h3 mb-3">{t('cowork.title')}</h1>
        <div className="alert alert-warning">
          <strong>{t('cowork.forbidden.title')}</strong>
          <p className="mb-0">{t('cowork.forbidden.body')}</p>
        </div>
      </section>
    );
  }
  // Onboarding richiesto (riusa lo stesso guard del workspace).
  if (
    onboardingStatus.kind === 'loading' ||
    onboardingStatus.kind === 'incomplete' ||
    onboardingStatus.kind === 'error'
  ) {
    return (
      <main lang="it">
        <OnboardingRequiredScreen
          phase={onboardingStatus.kind}
          {...(onboardingStatus.kind === 'error'
            ? { errorMessage: onboardingStatus.message }
            : {})}
        />
      </main>
    );
  }

  return (
    <section
      className="urania-cowork container py-3"
      data-testid="cowork-page"
      aria-labelledby="cowork-page-heading"
    >
      <div
        className="rounded p-4 mb-3"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <span
          className="badge mb-2"
          style={{
            backgroundColor: 'rgba(255,255,255,0.18)',
            color: '#fff',
            fontSize: '0.7rem',
            letterSpacing: '0.05em',
          }}
        >
          {t('cowork.badge')}
        </span>
        <h1 id="cowork-page-heading" className="h3 mb-2">
          {t('cowork.title')}
        </h1>
        <p className="mb-0 opacity-90 small">{t('cowork.subtitle')}</p>
      </div>

      {sessionId === null ? (
        <TemplatePicker
          onPicked={(tipoId): void => createMutation.mutate(tipoId)}
          isCreating={createMutation.isPending}
          createError={createError}
        />
      ) : sessionQuery.isLoading ? (
        <p role="status" aria-live="polite">
          {t('cowork.loading')}
        </p>
      ) : sessionQuery.isError ? (
        <div className="alert alert-danger" role="alert">
          {sessionQuery.error.detail ?? sessionQuery.error.message}
        </div>
      ) : sessionQuery.isSuccess ? (
        <div className="row g-3">
          <div className="col-lg-8">
            <ChatPanel
              session={sessionQuery.data}
              onSend={async (content): Promise<void> => {
                await sendMutation.mutateAsync(content);
              }}
              sending={sendMutation.isPending}
              sendError={sendError}
            />
          </div>
          <div className="col-lg-4 d-flex flex-column gap-3">
            <AttachmentsPanel
              session={sessionQuery.data}
              disabled={sessionQuery.data.status === 'drafting'}
              onUpload={async (file, motivo): Promise<void> => {
                await uploadMutation.mutateAsync({ file, motivo });
              }}
            />
            <DocumentPanel
              session={sessionQuery.data}
              generating={generateMutation.isPending || sessionQuery.data.status === 'drafting'}
              generateError={generateError}
              onGenerate={async (): Promise<void> => {
                await generateMutation.mutateAsync();
              }}
              onDownload={onDownload}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/cowork',
  component: CoworkPage,
});
